import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, ArrowLeft } from "lucide-react";
import { blogPosts } from "../data/blogPosts";
import ContactForm from "../components/ContactForm";
import { useEffect } from "react";

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  // Set document title manually instead of using Helmet
  useEffect(() => {
    if (post) {
      document.title = post.metaTitle || post.title || "Blog Post";
      // Set meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = post.metaDescription || post.excerpt || "";

      // Set canonical
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `https://wintrustsolutions.com/blog/${post.slug}`;
    }
    return () => {
      document.title = "WINTRUST SOLUTIONS";
    };
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const renderInline = (text) => {
    // Split on bold markers and EMI Calculator references
    const parts = text.split(/(\*\*[^*]+\*\*|EMI Calculator)/g);
    return parts.map((part, j) => {
      if (part === "EMI Calculator") {
        return <a key={j} href="/emi-calculator" className="text-gold font-semibold hover:underline">EMI Calculator</a>;
      }
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={j} className="text-navy">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderContent = () => {
    return post.content.split("\n").map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith("## ")) {
        return <h2 key={i} className="text-2xl font-semibold text-navy font-heading mt-8 mb-4">{renderInline(trimmed.replace("## ", ""))}</h2>;
      }
      if (trimmed.startsWith("### ")) {
        return <h3 key={i} className="text-xl font-semibold text-navy font-heading mt-6 mb-3">{renderInline(trimmed.replace("### ", ""))}</h3>;
      }
      if (trimmed.startsWith("- **")) {
        const parts = trimmed.replace("- **", "").split(":**");
        return (
          <div key={i} className="flex gap-2 ml-4 mb-2">
            <span className="text-gold mt-1.5 flex-shrink-0">&#8226;</span>
            <p className="text-slate-600"><strong className="text-navy">{parts[0]}:</strong>{renderInline(parts.slice(1).join(":**"))}</p>
          </div>
        );
      }
      if (trimmed.startsWith("- ")) {
        return (
          <div key={i} className="flex gap-2 ml-4 mb-2">
            <span className="text-gold mt-1.5 flex-shrink-0">&#8226;</span>
            <p className="text-slate-600">{renderInline(trimmed.replace("- ", ""))}</p>
          </div>
        );
      }
      if (trimmed.startsWith("|")) {
        return null;
      }
      if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
        return <p key={i} className="text-slate-700 font-semibold mb-3">{trimmed.replace(/\*\*/g, "")}</p>;
      }
      return <p key={i} className="text-slate-600 mb-4 leading-relaxed">{renderInline(trimmed)}</p>;
    });
  };

  return (
    <>
      <section className="bg-navy py-12">
        <div className="container-custom max-w-4xl">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link>
            {" / "}
            <Link to="/blog" className="hover:text-gold">Blog</Link>
            {" / "}
            <span className="text-white">{post.category}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 mt-4">
            <span className="text-xs font-semibold text-navy bg-gold px-3 py-1 rounded-full">
              {post.category}
            </span>
            <span className="text-slate-400 text-sm flex items-center gap-1">
              <Calendar size={14} />
              {post.date}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-white section-padding">
        <div className="container-custom max-w-4xl">
          <article className="prose prose-slate prose-lg max-w-none blog-content" data-testid="blog-post-content">
            {renderContent()}
          </article>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link
              to="/blog"
              className="text-gold font-medium hover:text-gold-dark inline-flex items-center gap-2"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={18} />
              Back to All Articles
            </Link>
          </div>
        </div>
      </section>

      <ContactForm />
    </>
  );
};

export default BlogPostPage;
